import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../supabase';
import { PrimaryBtn, SecondaryBtn } from '../systemKit';

// AddToWatchlistModal — searchable item picker. Fetches all food_items for
// the company once, filters in memory by normalized_name. Operator picks
// one + optionally adds a note ("watching since vendor switched in March").

export default function AddToWatchlistModal({
  open,
  companyId,
  onClose,
  onAdded,
  excludeFoodItemIds = [],
}) {
  const [items, setItems] = useState(null);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const excludeSet = useMemo(() => new Set(excludeFoodItemIds.map(Number)), [excludeFoodItemIds]);

  useEffect(() => {
    if (!open || !companyId) return;
    let cancelled = false;
    setQuery(''); setSelected(null); setNote(''); setItems(null);
    (async () => {
      const { data } = await supabase
        .from('food_items')
        .select('id, normalized_name')
        .eq('company_id', companyId)
        .order('normalized_name');
      if (cancelled) return;
      setItems(data || []);
    })();
    return () => { cancelled = true; };
  }, [open, companyId]);

  // ESC closes — parent handles backdrop click via the wrapper.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filtered = useMemo(() => {
    if (!items) return [];
    const q = query.trim().toLowerCase();
    return items
      .filter(it => !excludeSet.has(Number(it.id)))
      .filter(it => !q || (it.normalized_name || '').toLowerCase().includes(q))
      .slice(0, 50);
  }, [items, query, excludeSet]);

  const handleAdd = async () => {
    if (!selected || saving) return;
    setSaving(true);
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from('cost_watchlist_items').insert({
      company_id: companyId,
      food_item_id: selected.id,
      added_by: user?.id || null,
      notes: note || null,
    });
    setSaving(false);
    if (!error) {
      onAdded?.();
      onClose?.();
    }
  };

  if (!open) return null;

  return (
    <>
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 90 }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Add to watchlist"
        style={{
          position: 'fixed',
          top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 'min(520px, calc(100vw - 32px))',
          maxHeight: 'calc(100vh - 64px)',
          background: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: 12,
          boxShadow: '0 12px 48px rgba(0,0,0,0.25)',
          zIndex: 91,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--text-secondary)' }}>
              Watchlist
            </div>
            <div style={{ fontSize: 16, fontWeight: 600, color: 'var(--text-primary)' }}>
              Add item to track
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            style={{ border: 'none', background: 'transparent', color: 'var(--text-secondary)', fontSize: 18, cursor: 'pointer', padding: 4 }}
          >
            ✕
          </button>
        </div>

        <div style={{ padding: 16 }}>
          <input
            autoFocus
            type="text"
            placeholder="Search item by name..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{
              width: '100%', padding: '10px 12px', fontSize: 14,
              background: 'var(--bg-subtle)', border: '1px solid var(--border)',
              borderRadius: 8, color: 'var(--text-primary)',
            }}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '0 8px', minHeight: 200 }}>
          {!items && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              Loading…
            </div>
          )}
          {items && filtered.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>
              No items match.
            </div>
          )}
          {filtered.map(it => {
            const isSelected = selected?.id === it.id;
            return (
              <button
                key={it.id}
                onClick={() => setSelected(it)}
                style={{
                  width: '100%',
                  textAlign: 'left',
                  padding: '8px 12px',
                  border: 'none',
                  background: isSelected ? 'var(--bg-accent-chip)' : 'transparent',
                  color: isSelected ? 'var(--accent-primary)' : 'var(--text-primary)',
                  borderRadius: 6,
                  cursor: 'pointer',
                  fontSize: 13,
                  display: 'block',
                }}
                onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = 'var(--bg-hover)'; }}
                onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
              >
                {it.normalized_name || `Item #${it.id}`}
              </button>
            );
          })}
        </div>

        {selected && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border)', background: 'var(--bg-subtle)' }}>
            <div style={{ fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text-secondary)', marginBottom: 4 }}>
              Note (optional)
            </div>
            <input
              type="text"
              placeholder="e.g. watching since vendor switch in March"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              style={{
                width: '100%', padding: '8px 10px', fontSize: 13,
                background: 'var(--bg-card)', border: '1px solid var(--border)',
                borderRadius: 6, color: 'var(--text-primary)',
              }}
            />
          </div>
        )}

        <div style={{ padding: 12, borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <SecondaryBtn onClick={onClose}>Cancel</SecondaryBtn>
          <PrimaryBtn onClick={handleAdd} disabled={!selected || saving}>
            {saving ? 'Adding…' : 'Add to watchlist'}
          </PrimaryBtn>
        </div>
      </div>
    </>
  );
}
